import React from 'react';
import { ErrorMessage } from 'ui-toolkit';
import { useAppDispatch } from '../../store/store';
import { DefaultContactsPage } from './DefaultContactsPage';
import { IAddEditDefaultContactData } from '../../types/clients/CreateDefaultContact';
import { BaseAPI, convertToError } from '../../API/BaseApi';
import { eAPITags } from '../../API/eAPITags';
import {
  useListDefaultContactsQuery,
  useAddDefaultContactMutation,
  useUpdateDefaultContactMutation,
  useDeleteDefaultContactMutation,
  useAffectedOrdersForDefaultContactMutation,
} from '../../API/clients/ClientUsersApi';

interface IDefaultContactsContainerProps {
  clientId: string;
}

export const DefaultContactsContainer: React.FC<IDefaultContactsContainerProps> = ({ clientId }) => {
  const dispatch = useAppDispatch();

  const {
    data: defaultContacts = [],
    error: fetchError,
    isFetching,
  } = useListDefaultContactsQuery(clientId);

  const [addDefaultContact, addResult] = useAddDefaultContactMutation();
  const [updateDefaultContact, updateResult] = useUpdateDefaultContactMutation();
  const [deleteDefaultContact, deleteResult] = useDeleteDefaultContactMutation();
  const [calculateAffectedOrders, affectedOrdersResult] = useAffectedOrdersForDefaultContactMutation();

  const onResetUpdate = () => {
    updateResult.reset();
    deleteResult.reset();
    affectedOrdersResult.reset();
  };

  const onResetAdd = () => {
    addResult.reset();
    affectedOrdersResult.reset();
  };

  const onEditDefaultContact = async (contact: IAddEditDefaultContactData) => {
    if (!contact.updateOrders) {
      affectedOrdersResult.reset();
    }

    let result;

    if (contact.updateType === 'UPDATE') {
      result = await updateDefaultContact({ clientId, contact });
    } else if (contact.updateType === 'DELETE') {
      result = await deleteDefaultContact({ clientId, contact });
    } else {
      result = await addDefaultContact({ clientId, contact });
    }

    // Invalidate internal contacts cache when orders were updated
    // This ensures that any open order pages will refresh their internal contacts tab
    // to reflect the changes made by the default contact update/delete/add operation
    if ('data' in result && contact.updateOrders === true) {
      dispatch(BaseAPI.util.invalidateTags([{ type: eAPITags.OrderInternalContacts }]));
    }
  };

  const getPreliminaryAffectedOrders = (contact: IAddEditDefaultContactData) => {
    calculateAffectedOrders({ clientId, contact });
  };

  return (
    <>
      <ErrorMessage error={convertToError(fetchError)} />
      <DefaultContactsPage
        defaultContacts={defaultContacts}
        loading={isFetching}
        onResetAdd={onResetAdd}
        addFetchStatus={{
          fetching: addResult.isLoading,
          fetched: addResult.isSuccess || addResult.isError,
          success: addResult.isSuccess,
        }}
        addFetchError={convertToError(addResult.error)}
        deleteFetchStatus={{
          fetching: deleteResult.isLoading,
          fetched: deleteResult.isSuccess || deleteResult.isError,
          success: deleteResult.isSuccess,
        }}
        deleteFetchError={convertToError(deleteResult.error)}
        onResetUpdate={onResetUpdate}
        onAddUpdateDefaultContact={onEditDefaultContact}
        updateFetchStatus={{
          fetching: updateResult.isLoading,
          fetched: updateResult.isSuccess || updateResult.isError,
          success: updateResult.isSuccess,
        }}
        updateFetchError={convertToError(updateResult.error)}
        clientId={clientId}
        getAffectedOrders={getPreliminaryAffectedOrders}
        affectedOrderFetchStatus={{
          fetching: affectedOrdersResult.isLoading,
          fetched: affectedOrdersResult.isSuccess || affectedOrdersResult.isError,
          success: affectedOrdersResult.isSuccess,
        }}
        affectedOrders={affectedOrdersResult.data?.affectedOrders ?? 0}
      />
    </>
  );
};
